import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { capitalizeFirstLetter } from '../../utils/capitalizeFirstLetter';
import moment from 'moment';
import '../../pages/Reports/reports.css';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2})?.*)?$/;
const formatValue = (value) => {
  if (value && typeof value === 'string' && ISO_DATE_RE.test(value)) {
    return moment(value).format('MM/DD/YYYY');
  }
  return value;
};

// Fields that are redundant in the row body (already shown as section/sub headers)
const SKIP_FIELDS = new Set(['name', 'pay_code']);

export const OvertimeTable = ({ tableData, headerItems }) => {
  if (!tableData?.length) return null;

  const bodyHeaders = headerItems.filter((hi) => !SKIP_FIELDS.has(hi[0]));

  return (
    <>
      {tableData.map((it, idx) => {
        const splittedLines = it[1].reduce((acc, item) => {
          if (!acc[item.pay_code]) acc[item.pay_code] = [];
          acc[item.pay_code].push(item);
          return acc;
        }, {});
        const splittedArr = Object.entries(splittedLines);

        return (
          <Box key={`section-${idx}`} className="page-break-reports">
            {/* Person / group header */}
            <Typography
              variant="h6"
              sx={{
                mt: 3,
                mb: 1.5,
                fontWeight: 700,
                pb: 0.75,
                borderBottom: '2px solid',
                borderColor: 'grey.300',
              }}
            >
              {it[0]}
            </Typography>

            {splittedArr.map(([payCode, items], payIdx) => {
              const totalHours = items.reduce((s, item) => {
                const v = +item['hours'];
                return !Number.isNaN(v) ? s + v : s;
              }, 0);
              const totalAmount = items.reduce((s, item) => {
                const v = +item['amount'];
                return !Number.isNaN(v) ? s + v : s;
              }, 0);
              const hasHours  = headerItems.some((h) => h[0] === 'hours');
              const hasAmount = headerItems.some((h) => h[0] === 'amount');

              return (
                <Box key={`paycode-${payIdx}`} sx={{ mb: 3 }}>
                  {/* Pay-code pill */}
                  {payCode && (
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        bgcolor: '#BF1D2D',
                        borderRadius: '4px 4px 0 0',
                        display: 'inline-flex',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700, letterSpacing: 0.5 }}>
                        {payCode}
                      </Typography>
                    </Box>
                  )}

                  {/* Record cards */}
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'grey.200',
                      borderRadius: payCode ? '0 4px 4px 4px' : 1,
                      overflow: 'hidden',
                    }}
                  >
                    {items.map((item, i) => (
                      <Box key={`row-${i}`}>
                        {/* Row header: number */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 2,
                            pt: 1.25,
                            pb: 0.5,
                            bgcolor: i % 2 === 0 ? '#fafafa' : '#fff',
                            borderTop: i > 0 ? '2px solid' : 'none',
                            borderColor: 'grey.300',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '12px',
                              fontWeight: 800,
                              color: '#BF1D2D',
                              letterSpacing: 0.3,
                              userSelect: 'none',
                            }}
                          >
                            #{i + 1}
                          </Typography>
                        </Box>

                        {/* Fields grid */}
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(165px, 1fr))',
                            gap: 2,
                            px: 2,
                            pb: 1.75,
                            bgcolor: i % 2 === 0 ? '#fafafa' : '#fff',
                          }}
                        >
                          {bodyHeaders.map((hi, indx) => (
                            <Box key={`cell-${i}-${indx}`}>
                              <Typography
                                sx={{
                                  display: 'block',
                                  color: 'text.secondary',
                                  fontSize: '12px',
                                  lineHeight: 1.4,
                                  mb: 0.3,
                                }}
                              >
                                {hi[1]}
                              </Typography>
                              <Typography
                                sx={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.4 }}
                                color={
                                  item[hi[0]] === 'Filled'
                                    ? 'success.main'
                                    : item[hi[0]] === 'Ordered'
                                    ? 'error.main'
                                    : 'text.primary'
                                }
                              >
                                {hi[0] === 'type'
                                  ? capitalizeFirstLetter(item[hi[0]])
                                  : formatValue(item[hi[0]]) ?? '—'}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ))}

                    {/* Totals footer */}
                    {(hasHours || hasAmount) && (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 3,
                          px: 1.5,
                          py: 0.9,
                          bgcolor: 'grey.100',
                          borderTop: '2px solid',
                          borderColor: 'grey.300',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {items.length} {items.length === 1 ? 'record' : 'records'}
                        </Typography>
                        {hasHours && (
                          <Typography variant="caption" fontWeight={700}>
                            Total Hours:{' '}
                            <span style={{ color: '#000' }}>{+totalHours.toFixed(2)}</span>
                          </Typography>
                        )}
                        {hasAmount && (
                          <Typography variant="caption" fontWeight={700}>
                            Total Amount:{' '}
                            <span style={{ color: '#000' }}>{+totalAmount.toFixed(2)}</span>
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        );
      })}
    </>
  );
};
